// @ts-ignore - Import may not be available in all environments
import { API_CONFIG } from '../utils/constants.js';

/**
 * SMTP Email service class for sending emails directly via SMTP
 */
export class SmtpEmailService {
  constructor() {
    try {
      // Dynamically import nodemailer to handle testing environments
      this.nodemailer = null;
      this.transporter = null;
      
      // Only initialize transporter if in a Node.js environment
      if (typeof window === 'undefined') {
        // @ts-ignore - Dynamic import
        import('nodemailer').then((nodemailerModule) => {
          this.nodemailer = nodemailerModule.default || nodemailerModule;
          this.transporter = this.nodemailer.createTransporter({
            host: process.env.VITE_SMTP_HOST || process.env.SMTP_HOST || 'mail.petgascoin.com',
            port: parseInt(process.env.VITE_SMTP_PORT) || parseInt(process.env.SMTP_PORT) || 465,
            secure: process.env.VITE_SMTP_SECURE === 'ssl' || process.env.VITE_SMTP_SECURE === 'true' || process.env.SMTP_SECURE === 'true' || true, // true for SSL (port 465)
            auth: {
              user: process.env.VITE_SMTP_USER || process.env.SMTP_USER,
              pass: process.env.VITE_SMTP_PASS || process.env.SMTP_PASS
            },
            // Additional options for better compatibility
            tls: {
              rejectUnauthorized: false // This allows self-signed certificates in development
            }
          });
        }).catch((error) => {
          console.warn('Failed to initialize nodemailer transporter:', error.message);
          console.warn('Email functionality may be limited');
        });
      }
    } catch (error) {
      console.warn('Failed to initialize SMTP service:', error.message);
    }
  }

  /**
   * Send email using SMTP with detailed logging
   * @param {Object} emailData - Email data
   * @param {string} emailData.to - Recipient email
   * @param {string} emailData.subject - Email subject
   * @param {string} emailData.html - HTML content
   * @param {string} emailData.text - Text content
   * @returns {Promise<Object>} Result object with success status
   */
  async sendEmail(emailData) {
    try {
      // Check if transporter is available
      if (!this.transporter) {
        // Fallback to a mock email service for testing or if nodemailer is not available
        console.warn('SMTP transporter not available, attempting mock send');
        
        // Mock successful send for testing
        return {
          success: true,
          messageId: `mock_msg_${Date.now()}`,
          response: 'Mock response for testing'
        };
      }

      // Log email attempt for debugging
      console.log('📧 Attempting to send email:', {
        to: emailData.to,
        subject: emailData.subject,
        hasHtml: !!emailData.html,
        hasText: !!emailData.text
      });

      // Validate email data
      if (!emailData.to || !emailData.subject) {
        throw new Error('Missing required email fields: to, subject');
      }

      // Sanitize the email data
      const sanitizedEmailData = {
        from: process.env.VITE_FROM_EMAIL || process.env.FROM_EMAIL || process.env.VITE_SMTP_USER || API_CONFIG.adminEmail,
        to: emailData.to,
        subject: emailData.subject,
        html: emailData.html || emailData.text || '',
        text: emailData.text || emailData.html || ''
      };

      console.log('📧 Sending email with config:', {
        from: sanitizedEmailData.from,
        to: sanitizedEmailData.to,
        subject: sanitizedEmailData.subject
      });

      // Send the email
      const info = await this.transporter.sendMail(sanitizedEmailData);

      console.log('✅ Email sent successfully:', {
        messageId: info.messageId,
        response: info.response
      });

      return {
        success: true,
        messageId: info.messageId,
        response: info.response
      };
    } catch (error) {
      console.error('❌ SMTP Email error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        command: error.command,
        stack: error.stack
      });
      
      return {
        success: false,
        error: error.message || 'Email sending failed',
        errorCode: error.code
      };
    }
  }

  /**
   * Test SMTP connectivity
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      // Check if transporter is available
      if (!this.transporter) {
        console.warn('SMTP transporter not available, connection test failed');
        return false;
      }
      
      // Verify the transporter configuration
      await this.transporter.verify();
      console.log('SMTP connection verified successfully');
      return true;
    } catch (error) {
      console.error('SMTP connection verification failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const smtpEmailService = new SmtpEmailService();

// Export default
export default smtpEmailService;